import { AddSquareIcon, TrashAltIcon } from "assets/icons";
import cn from "classnames";
import { Media } from "components";
import {
  IMAGE_FORMATS,
  VIDEO_FORMATS,
  MAX_IMAGE_FILE_SIZE,
  MAX_VIDEO_FILE_SIZE,
  MAX_DOCUMENT_FILE_SIZE,
  DOCUMENT_FORMATS,
  BYTES_IN_MB
} from "constants/common";
import { MediaType } from "constants/enums";
import { getAcceptObjectForFilesInput, handleUploadFileError } from "helpers";
import { useImagesCompressor, useVideoCompressor } from "hooks";
import { FileRejection, useDropzone } from "react-dropzone";
import { ClipLoader } from "react-spinners";

import { MediaFilePickerProps } from "./MediaFilePicker.types";

import styles from "./MediaFilePicker.module.scss";

const MediaFilePicker = ({
  label,
  allowedMediaTypes = [MediaType.IMAGE, MediaType.VIDEO],
  size = "md",
  value,
  variant = "rectangle",
  acceptImageFormats = IMAGE_FORMATS,
  acceptVideoFormats = VIDEO_FORMATS,
  acceptDocumentFormats = DOCUMENT_FORMATS,
  loadingFilesCounter,
  customSize,
  customMaxFileSizeInMB,
  maxFiles = 1,
  withCompression = true,
  onDeleteMediaFile,
  onFilesDrop,
  compressionOptions,
  mediaClassName,
  "data-testid": testId
}: MediaFilePickerProps) => {
  const isImagesAllowed = allowedMediaTypes.includes(MediaType.IMAGE);
  const isVideosAllowed = allowedMediaTypes.includes(MediaType.VIDEO);
  const isDocumentsAllowed = allowedMediaTypes.includes(MediaType.DOCUMENT);

  const { compressImages, compressingImagesCount } =
    useImagesCompressor(compressionOptions);
  const {
    compressVideos,
    status: videoStatus,
    progress: videoProgress
  } = useVideoCompressor();
  const isProcessingVideo = videoStatus !== "idle";

  const onDropImageRejected = (files: FileRejection[]) => {
    const file = files[0];
    const message = file.errors[0].message;
    const code = file.errors[0].code;

    handleUploadFileError(
      message,
      code,
      allowedMediaTypes,
      customMaxFileSizeInMB
    );
  };

  const handleDropAccepted = async (files: File[]) => {
    if (withCompression) {
      const compressed = await compressImages(files);
      const processed = await compressVideos(compressed);
      onFilesDrop(processed);
    } else {
      onFilesDrop(files);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: getAcceptObjectForFilesInput({
      acceptImageFormats,
      acceptVideoFormats,
      acceptDocumentFormats,
      isImagesAllowed,
      isVideosAllowed,
      isDocumentsAllowed
    }),
    maxSize: customMaxFileSizeInMB
      ? customMaxFileSizeInMB * BYTES_IN_MB
      : allowedMediaTypes.includes(MediaType.VIDEO)
        ? MAX_VIDEO_FILE_SIZE
        : allowedMediaTypes.includes(MediaType.IMAGE)
          ? MAX_IMAGE_FILE_SIZE
          : MAX_DOCUMENT_FILE_SIZE,
    maxFiles,
    noDrag: true,
    noKeyboard: true,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onDropAccepted: handleDropAccepted,
    onDropRejected: onDropImageRejected
  });

  const customSizeStyle = customSize
    ? {
        width: customSize.width === "full" ? "100%" : `${customSize.width}px`,
        height: `${customSize.height}px`
      }
    : {};

  return (
    <div className={styles.imagesPicker} data-testid={testId}>
      {value ? (
        <div
          key={value?.id}
          style={customSizeStyle}
          className={cn(
            styles.imagesPicker_imageThumb,
            styles[`imagesPicker-${variant}-${size}`]
          )}
        >
          <>
            <Media
              className={cn(
                styles.imagesPicker_image,
                styles[`imagesPicker_image-${variant}`]
              )}
              mediaClassName={mediaClassName}
              mediaFile={value}
              controls
            />
          </>
          <button
            className={styles.imagesPicker_deleteBtn}
            onClick={onDeleteMediaFile}
          >
            <TrashAltIcon width={16} heigh={16} fill={"#FFFFFF"} />
          </button>
        </div>
      ) : loadingFilesCounter > 0 ||
        compressingImagesCount > 0 ||
        isProcessingVideo ? (
        <div
          style={customSizeStyle}
          className={cn(
            styles.imagesPicker_addBtn,
            styles[`imagesPicker_addBtn-${variant}`],
            styles[`imagesPicker-${variant}-${size}`],
            styles.imagesPicker_addBtn__loading
          )}
        >
          {isProcessingVideo ? (
            <div style={{ width: "80%", textAlign: "center" }}>
              <p
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#FB3973",
                  marginBottom: 6
                }}
              >
                {videoStatus === "loading"
                  ? "Preparing..."
                  : `Optimizing video... ${videoProgress}%`}
              </p>
              <div
                style={{
                  width: "100%",
                  height: 6,
                  borderRadius: 6,
                  backgroundColor: "#F6F6FA"
                }}
              >
                <div
                  style={{
                    width: `${videoProgress}%`,
                    height: "100%",
                    borderRadius: 6,
                    backgroundColor: "#FB3973",
                    transition: "width 0.3s"
                  }}
                />
              </div>
            </div>
          ) : (
            <ClipLoader size={20} color="#FB3973" />
          )}
        </div>
      ) : (
        <div
          {...getRootProps()}
          style={customSizeStyle}
          className={cn(
            styles.imagesPicker_addBtn,
            styles[`imagesPicker_addBtn-${variant}`],
            styles[`imagesPicker-${variant}-${size}`]
          )}
        >
          <input {...getInputProps()} style={{ display: "none" }} />
          <AddSquareIcon color="#FB3973" width={20} height={20} />
          <pre>{label}</pre>
        </div>
      )}
    </div>
  );
};

export default MediaFilePicker;
